import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useSubscription } from '@/hooks/account/useBilling';

/**
 * The dunning strip's state — ROUTE_INVENTORY Part 1, G9.
 *
 * Two recoverable states, so a failing renewal does not silently drop the
 * user to Free:
 *
 *   - **action-required** — the renewal needs customer authentication (SCA /
 *     OTP, common on Indian cards), completed on Stripe's hosted invoice.
 *     Outranks past due: a declined renewal that Stripe retries with 3DS
 *     reaches both flags, and only the challenge clears it.
 *   - **past-due** — the charge was declined and Stripe is retrying; the fix
 *     is a new card in Settings → Payment.
 *
 * Dismissal is in memory only: it survives a route change, not a reload, so a
 * critical alert is never lost for good. On `/auth` the subscription is not
 * even requested — the user is signed out and it would 401.
 */
export interface BillingBannerState {
  kind: 'action-required' | 'past-due' | null;
  /** Stripe's hosted invoice, when the action is required. */
  hostedUrl: string | null;
  dismissed: boolean;
  dismiss: () => void;
}

export function useBillingBanner(): BillingBannerState {
  const location = useLocation();
  const isAuthPage = location.pathname.startsWith('/auth');
  const { data } = useSubscription({ enabled: !isAuthPage });
  const [dismissed, setDismissed] = useState(false);

  const sub = isAuthPage ? null : (data?.subscription ?? null);
  const kind = !sub
    ? null
    : sub.payment_action_required
      ? 'action-required'
      : sub.status === 'past_due'
        ? 'past-due'
        : null;

  return {
    kind,
    hostedUrl: sub?.action_required_invoice_url ?? null,
    dismissed,
    dismiss: () => setDismissed(true),
  };
}
